import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Palette, Upload, Trash2, Compass, Link2, Copy, ExternalLink, QrCode, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const MAX_BYTES = 500 * 1024;

export const CoBrandBar = ({ brand, compact = false }) => (
  <div className={`flex items-center justify-between gap-4 min-w-0 ${compact ? "" : "px-1"}`} data-testid="cobrand-bar">
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0"><Compass className="w-4 h-4 text-white" /></div>
      <span className="font-heading font-extrabold text-slate-900 text-sm sm:text-base truncate">Motley Mentors <span className="text-slate-400 font-medium">·</span> CareerCompass</span>
    </div>
    {brand?.name && (
      <div className="flex items-center gap-2 text-right min-w-0" data-testid="cobrand-partner">
        <span className="font-body text-[10px] uppercase tracking-wide text-slate-400 hidden sm:inline shrink-0">in partnership with</span>
        {brand.logo && <img src={brand.logo} alt={brand.name} className="h-8 max-w-[120px] object-contain rounded shrink-0" data-testid="cobrand-logo" />}
        <span className="font-heading font-bold text-slate-800 text-sm truncate">{brand.name}</span>
      </div>
    )}
  </div>
);

export const PartnerLinkSection = () => {
  const { user, setUser } = useAuth();
  const [slug, setSlug] = useState(user?.partner_slug || "");
  const [tagline, setTagline] = useState(user?.partner_tagline || "");
  const [busy, setBusy] = useState(false);
  const url = `${window.location.origin}/p/${user?.partner_slug || ""}`;
  const dirty = slug !== (user?.partner_slug || "") || tagline !== (user?.partner_tagline || "");

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/partner-page", { slug, tagline });
      setUser(res.data); setSlug(res.data.partner_slug); toast.success("Partner page updated");
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not update"); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Partner page link copied"); } catch (e) { window.prompt("Copy:", url); } };
  const [qrBusy, setQrBusy] = useState(false);
  const downloadQr = async () => {
    setQrBusy(true);
    try {
      const res = await api.get("/admin/partner-page/qr", { params: { base_url: window.location.origin, size: 1024 }, responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = `CareerCompass-QR-${user?.partner_slug || "partner"}.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
      toast.success("QR code downloaded (1024px PNG)");
    } catch (e) { toast.error("Could not generate QR code"); }
    finally { setQrBusy(false); }
  };
  const qrPreview = `${process.env.REACT_APP_BACKEND_URL}/api/admin/partner-page/qr?base_url=${encodeURIComponent(window.location.origin)}&size=256&v=${encodeURIComponent(user?.partner_slug || "")}`;

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="partner-link-section">
      <div className="flex items-center gap-2">
        <Link2 className="w-4 h-4 text-primary" />
        <p className="font-heading font-bold text-sm text-slate-900">Your branded landing page</p>
      </div>
      <p className="font-body text-xs text-slate-500">Share this link with candidates — they'll see your brand alongside Motley Mentors and enter their code there.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate" data-testid="partner-url">{url}</code>
        <Button type="button" size="sm" variant="outline" className="rounded-lg h-8" onClick={copy} title="Copy link" data-testid="partner-copy-btn"><Copy className="w-3.5 h-3.5" /></Button>
        <Button type="button" size="sm" variant="outline" className="rounded-lg h-8" asChild title="Open page" data-testid="partner-open-btn">
          <a href={url} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="font-heading font-bold text-slate-800 text-xs">Link name</Label>
          <div className="flex items-center gap-1 mt-1">
            <span className="font-mono text-xs text-slate-400">/p/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} className="rounded-lg h-9 font-mono text-xs" data-testid="partner-slug-input" />
          </div>
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800 text-xs">Tagline (optional)</Label>
          <Input value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={140} placeholder="Career clarity for mid-career professionals" className="rounded-lg h-9 mt-1 text-xs" data-testid="partner-tagline-input" />
        </div>
      </div>
      {dirty && (
        <Button type="button" size="sm" onClick={save} disabled={busy} className="rounded-lg font-heading font-bold" data-testid="partner-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save link
        </Button>
      )}
      <div className="flex items-center gap-4 pt-2 border-t border-slate-100" data-testid="partner-qr-section">
        <img src={qrPreview} alt="QR code for your partner page" className="w-20 h-20 rounded-lg border border-slate-200" data-testid="partner-qr-preview" />
        <div className="flex-1">
          <p className="font-heading font-bold text-sm text-slate-900 flex items-center gap-1.5"><QrCode className="w-4 h-4 text-primary" /> QR code for print</p>
          <p className="font-body text-xs text-slate-500 mt-0.5">Put it on flyers, slides or posters — scanning opens your landing page.</p>
          <Button type="button" size="sm" variant="outline" onClick={downloadQr} disabled={qrBusy} className="rounded-lg mt-2 font-heading font-bold" data-testid="partner-qr-download-btn">
            {qrBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Download PNG
          </Button>
        </div>
      </div>
    </div>
  );
};

export const BrandDialog = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.brand_name || "");
  const [logo, setLogo] = useState(user?.brand_logo || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp|gif|svg\+xml)$/.test(f.type)) { toast.error("Use a PNG, JPG, WEBP, GIF or SVG image."); return; }
    if (f.size > MAX_BYTES) { toast.error("Logo must be under 500 KB."); return; }
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result || ""));
    r.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/profile", { brand_name: name.trim(), brand_logo: logo });
      setUser(res.data);
      toast.success(name.trim() || logo ? "Co-branding saved — it now appears on your candidates' reports and emails" : "Co-branding removed");
      setOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save branding");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setName(user?.brand_name || ""); setLogo(user?.brand_logo || ""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-xl font-heading font-bold" data-testid="brand-btn">
          <Palette className="w-4 h-4 mr-1" /> {user?.brand_name || user?.brand_logo ? "Co-branding" : "Add my brand"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="brand-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">Co-brand your candidates' reports</DialogTitle>
          <DialogDescription className="font-body">
            Your name and logo appear alongside Motley Mentors · CareerCompass on every report and email your candidates receive. The Motley Mentors mark always stays.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="font-body text-[10px] uppercase tracking-wide text-slate-400 mb-2">Preview</p>
            <CoBrandBar brand={{ name: name.trim() || (logo ? user?.name : ""), logo }} compact />
          </div>
          <div>
            <Label className="font-heading font-bold text-slate-800">Brand / organisation name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={user?.name || "Your organisation"} className="mt-1.5 rounded-xl" data-testid="brand-name-input" />
          </div>
          <div>
            <Label className="font-heading font-bold text-slate-800">Logo (PNG/JPG/SVG, under 500 KB)</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={onFile} className="hidden" data-testid="brand-logo-input" />
              <Button type="button" variant="outline" className="rounded-xl font-heading font-bold" onClick={() => fileRef.current?.click()} data-testid="brand-upload-btn">
                <Upload className="w-4 h-4 mr-1" /> {logo ? "Replace logo" : "Upload logo"}
              </Button>
              {logo && (
                <Button type="button" variant="ghost" className="rounded-xl text-destructive hover:text-destructive" onClick={() => setLogo("")} data-testid="brand-remove-logo-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy} className="rounded-xl font-heading font-bold w-full" data-testid="brand-save-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save co-branding
            </Button>
          </DialogFooter>
          <PartnerLinkSection />
        </div>
      </DialogContent>
    </Dialog>
  );
};
